import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, Copy, Lock } from "lucide-react";
import { useState } from "react";
import { adminApi } from "@/api/admin.api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { errorMessage, formatDay, isPassPlan } from "./first-round";
import { useAdminProduct } from "./hooks";

// datetime-local inputs expect a LOCAL-time string (YYYY-MM-DDTHH:mm).
// Using toISOString() (UTC) would cause timezone-shifted validFrom values in non-UTC browsers.
const toLocalDatetimeInput = (d: Date) => {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
    d.getHours()
  )}:${pad(d.getMinutes())}`;
};

type Kind = "free_pass" | "discount";

const emptyForm = (kind: Kind) => ({
  kind,
  code: "",
  planId: "",
  notes: "",
  discountType: "PERCENTAGE",
  discountValue: "10",
  durationType: "ONCE",
  validFrom: toLocalDatetimeInput(new Date()),
  validUntil: "",
  maxRedemptions: kind === "free_pass" ? "10" : "",
  maxPerUser: "1",
});

const SUPER_ADMIN_ONLY = "Only Super Admins can create or change codes.";
const friendlyError = (e: unknown) => {
  const message = errorMessage(e);
  return /forbidden/i.test(message) ? SUPER_ADMIN_ONLY : message;
};

/** What a code gives, in words. */
const describeCoupon = (c: any, planNames: Record<string, string>) => {
  if (c.metadata?.kind === "free_pass" || (Number(c.discountValue) >= 100 && c.planIds?.length)) {
    const plan = planNames[c.planIds?.[0]] ?? c.metadata?.planName ?? "pass";
    return `Free ${plan} pass, no card`;
  }
  return c.discountType === "PERCENTAGE"
    ? `${Number(c.discountValue)}% off`
    : `$${Number(c.discountValue).toFixed(2)} off`;
};

export function CouponsManagement() {
  const qc = useQueryClient();
  const { toast } = useToast();
  const { product } = useAdminProduct();
  const isResume = product === "THERESUME";
  const { data: coupons } = useQuery({
    queryKey: ["coupons", product],
    queryFn: () => adminApi.listCoupons({ product }),
  });
  const { data: plans = [] } = useQuery({
    queryKey: ["plans", "THERESUME"],
    queryFn: () => adminApi.listPlans("THERESUME"),
    enabled: isResume,
  });
  const passPlans = (plans as any[]).filter(isPassPlan);
  const planNames = Object.fromEntries((plans as any[]).map((p) => [p.id, p.displayName]));

  const [form, setForm] = useState(() => emptyForm(isResume ? "free_pass" : "discount"));
  const [createError, setCreateError] = useState<string | null>(null);
  const [created, setCreated] = useState<any | null>(null);
  const [editing, setEditing] = useState<any | null>(null);
  const [viewing, setViewing] = useState<any | null>(null);
  const kind: Kind = isResume ? form.kind : "discount";
  const field = (key: string, val: string) => setForm((f) => ({ ...f, [key]: val }));

  const invalidate = () => qc.invalidateQueries({ queryKey: ["coupons"] });
  const update = useMutation({
    mutationFn: (payload: { id: string; dto: any }) =>
      adminApi.updateCoupon(payload.id, payload.dto),
    onSuccess: invalidate,
    onError: (e) =>
      toast({ title: "Couldn't update the code", description: friendlyError(e), variant: "destructive" }),
  });
  const create = useMutation({
    mutationFn: (dto: any) => adminApi.createCoupon(dto),
    onSuccess: (coupon) => {
      invalidate();
      setCreated(coupon);
      setForm(emptyForm(kind));
      setCreateError(null);
    },
    onError: (e) => setCreateError(friendlyError(e)),
  });

  const handleCreate = () => {
    setCreateError(null);
    setCreated(null);
    if (!form.code.trim()) return setCreateError("Enter a code.");
    const common: any = {
      code: form.code.trim(),
      validFrom: new Date(form.validFrom).toISOString(),
      validUntil: form.validUntil ? new Date(form.validUntil).toISOString() : null,
      maxRedemptions: form.maxRedemptions === "" ? null : Number(form.maxRedemptions),
      notes: form.notes,
    };
    if (kind === "free_pass") {
      if (!form.planId) return setCreateError("Choose the pass this code gives.");
      return create.mutate({ ...common, kind, planId: form.planId });
    }
    create.mutate({
      ...common,
      kind,
      product,
      discountType: form.discountType,
      discountValue: Number(form.discountValue),
      durationType: isResume ? "ONCE" : form.durationType,
      maxPerUser: Number(form.maxPerUser) || 1,
    });
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-semibold">
          Codes · {isResume ? "theResume (First Round)" : "theRecruit"}
        </h1>
        <p className="mt-1 flex items-center gap-1 text-sm text-gray-600">
          <Lock className="h-3.5 w-3.5" /> {SUPER_ADMIN_ONLY}
        </p>
      </div>

      {/* Create form */}
      <div className="border rounded-lg p-5 space-y-4 bg-white">
        <h2 className="font-medium">New code</h2>

        {isResume && (
          <div className="flex flex-wrap gap-2" role="radiogroup" aria-label="Kind of code">
            {(
              [
                [
                  "free_pass",
                  "Free pass (no card)",
                  "Gives a Starter or Active pass on its own. It starts the day it's used.",
                ],
                ["discount", "Discount at checkout", "Takes money off a pass bought by card."],
              ] as const
            ).map(([value, label, hint]) => (
              <button
                key={value}
                type="button"
                role="radio"
                aria-checked={form.kind === value}
                onClick={() => setForm(emptyForm(value))}
                className={`rounded border px-3 py-2 text-left text-sm ${
                  form.kind === value ? "border-slate-900 bg-slate-50" : "border-slate-200"
                }`}
              >
                <span className="block font-medium">{label}</span>
                <span className="block text-xs text-slate-500">{hint}</span>
              </button>
            ))}
          </div>
        )}

        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
          <FormField label="Code *">
            <input
              className="border rounded px-2 py-1.5 text-sm uppercase placeholder:normal-case"
              placeholder="e.g. MICHAEL"
              value={form.code}
              onChange={(e) => field("code", e.target.value)}
            />
          </FormField>

          {kind === "free_pass" ? (
            <FormField label="Gives *">
              <select
                aria-label="Pass the code gives"
                className="border rounded px-2 py-1.5 text-sm"
                value={form.planId}
                onChange={(e) => field("planId", e.target.value)}
              >
                <option value="">Choose a pass</option>
                {passPlans.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.displayName} · {p.accessDays} days
                  </option>
                ))}
              </select>
            </FormField>
          ) : (
            <>
              <FormField label="Discount type *">
                <select
                  className="border rounded px-2 py-1.5 text-sm"
                  value={form.discountType}
                  onChange={(e) => field("discountType", e.target.value)}
                >
                  <option value="PERCENTAGE">Percentage (%)</option>
                  <option value="FIXED_AMOUNT">Fixed amount ($)</option>
                </select>
              </FormField>
              <FormField
                label={form.discountType === "PERCENTAGE" ? "Discount % *" : "Discount $ *"}
              >
                <input
                  className="border rounded px-2 py-1.5 text-sm"
                  type="number"
                  min={0}
                  max={form.discountType === "PERCENTAGE" ? 100 : undefined}
                  value={form.discountValue}
                  onChange={(e) => field("discountValue", e.target.value)}
                />
              </FormField>
              {!isResume && (
                <FormField label="Applies for *">
                  <select
                    className="border rounded px-2 py-1.5 text-sm"
                    value={form.durationType}
                    onChange={(e) => field("durationType", e.target.value)}
                  >
                    <option value="ONCE">Once (first invoice only)</option>
                    <option value="REPEATING">Repeating (N months)</option>
                    <option value="FOREVER">Forever (every invoice)</option>
                  </select>
                </FormField>
              )}
            </>
          )}

          <FormField label={kind === "free_pass" ? "How many people can use it" : "Max total uses"}>
            <input
              className="border rounded px-2 py-1.5 text-sm"
              type="number"
              min={1}
              placeholder="Unlimited"
              value={form.maxRedemptions}
              onChange={(e) => field("maxRedemptions", e.target.value)}
            />
          </FormField>

          {kind === "discount" && (
            <FormField label="Uses per person">
              <input
                className="border rounded px-2 py-1.5 text-sm"
                type="number"
                min={1}
                value={form.maxPerUser}
                onChange={(e) => field("maxPerUser", e.target.value)}
              />
            </FormField>
          )}

          <FormField label="Valid from *">
            <input
              className="border rounded px-2 py-1.5 text-sm"
              type="datetime-local"
              value={form.validFrom}
              onChange={(e) => field("validFrom", e.target.value)}
            />
          </FormField>
          <FormField label="Expires on">
            <input
              className="border rounded px-2 py-1.5 text-sm"
              type="datetime-local"
              value={form.validUntil}
              onChange={(e) => field("validUntil", e.target.value)}
            />
          </FormField>
          <FormField label="Note (who it's for)">
            <input
              className="border rounded px-2 py-1.5 text-sm"
              placeholder="e.g. Michael's cohort"
              value={form.notes}
              onChange={(e) => field("notes", e.target.value)}
            />
          </FormField>
        </div>

        {kind === "free_pass" && (
          <p className="text-xs text-slate-500">
            One use per person. The pass starts the day the code is used.
          </p>
        )}
        {createError && <p className="text-sm text-red-600">{createError}</p>}
        {created && (
          <div
            role="status"
            className="flex flex-wrap items-center gap-2 rounded border border-green-200 bg-green-50 p-2 text-sm text-green-900"
          >
            <Check className="h-4 w-4" /> Created {created.code}.
            {created.shareLink && <CopyLink link={created.shareLink} />}
          </div>
        )}

        <button
          className="bg-slate-900 text-white text-sm px-4 py-2 rounded hover:bg-slate-700 disabled:opacity-50"
          onClick={handleCreate}
          disabled={create.isPending}
        >
          {create.isPending ? "Creating…" : "Create code"}
        </button>
      </div>

      {/* Table */}
      <div className="border rounded-lg overflow-hidden bg-white">
        <table className="w-full text-left text-sm">
          <thead className="bg-slate-50 border-b">
            <tr>
              <th className="px-4 py-3 font-medium text-slate-600">Code</th>
              <th className="px-4 py-3 font-medium text-slate-600">Gives</th>
              <th className="px-4 py-3 font-medium text-slate-600">Expires</th>
              <th className="px-4 py-3 font-medium text-slate-600">Used</th>
              <th className="px-4 py-3 font-medium text-slate-600">Status</th>
              <th className="px-4 py-3 font-medium text-slate-600">Actions</th>
            </tr>
          </thead>
          <tbody>
            {!coupons?.length && (
              <tr>
                <td colSpan={6} className="px-4 py-6 text-center text-slate-400">
                  No codes yet
                </td>
              </tr>
            )}
            {coupons?.map((c: any) => (
              <tr key={c.id} className="border-t hover:bg-slate-50 align-top">
                <td className="px-4 py-3">
                  <div className="font-mono font-semibold">{c.code}</div>
                  {c.notes && <div className="text-xs text-slate-500">{c.notes}</div>}
                </td>
                <td className="px-4 py-3">{describeCoupon(c, planNames)}</td>
                <td className="px-4 py-3 text-slate-500">{formatDay(c.validUntil)}</td>
                <td className="px-4 py-3 text-slate-500">
                  {c.currentRedemptions}
                  {c.maxRedemptions ? ` / ${c.maxRedemptions}` : ""}
                </td>
                <td className="px-4 py-3">
                  <span
                    className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${
                      c.isActive ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-700"
                    }`}
                  >
                    {c.isActive ? "Active" : "Off"}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <div className="flex flex-wrap gap-3">
                    <button
                      className="underline text-slate-600 hover:text-slate-900"
                      onClick={() => setViewing(c)}
                    >
                      Who used it
                    </button>
                    <button
                      className="underline text-slate-600 hover:text-slate-900"
                      onClick={() => setEditing(c)}
                    >
                      Edit
                    </button>
                    <button
                      className="underline text-slate-600 hover:text-slate-900 disabled:opacity-40"
                      onClick={() => update.mutate({ id: c.id, dto: { isActive: !c.isActive } })}
                      disabled={update.isPending}
                    >
                      {c.isActive ? "Turn off" : "Turn on"}
                    </button>
                    {isResume && <CopyLink link={c.shareLink} compact />}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <EditCouponDialog coupon={editing} onClose={() => setEditing(null)} onSaved={invalidate} />
      )}
      {viewing && <RedemptionsDialog coupon={viewing} onClose={() => setViewing(null)} />}
    </div>
  );
}

function EditCouponDialog({
  coupon,
  onClose,
  onSaved,
}: {
  coupon: any;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [maxRedemptions, setMax] = useState(
    coupon.maxRedemptions ? String(coupon.maxRedemptions) : ""
  );
  const [validUntil, setUntil] = useState(
    coupon.validUntil ? toLocalDatetimeInput(new Date(coupon.validUntil)) : ""
  );
  const [notes, setNotes] = useState(coupon.notes ?? "");
  const [error, setError] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: () =>
      adminApi.updateCoupon(coupon.id, {
        maxRedemptions: maxRedemptions === "" ? null : Number(maxRedemptions),
        validUntil: validUntil ? new Date(validUntil).toISOString() : null,
        notes,
      }),
    onSuccess: () => {
      onSaved();
      onClose();
    },
    onError: (e) => setError(friendlyError(e)),
  });

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit {coupon.code}</DialogTitle>
          <DialogDescription>
            {coupon.currentRedemptions}{" "}
            {coupon.currentRedemptions === 1 ? "person has" : "people have"} used it so far.
          </DialogDescription>
        </DialogHeader>
        <FormField label="How many people can use it (empty = unlimited)">
          <Input
            type="number"
            min={coupon.currentRedemptions || 1}
            value={maxRedemptions}
            onChange={(e) => setMax(e.target.value)}
          />
        </FormField>
        <FormField label="Expires on (empty = never)">
          <Input
            type="datetime-local"
            value={validUntil}
            onChange={(e) => setUntil(e.target.value)}
          />
        </FormField>
        <FormField label="Note">
          <Input value={notes} onChange={(e) => setNotes(e.target.value)} />
        </FormField>
        {error && <p className="text-sm text-red-600">{error}</p>}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button disabled={save.isPending} onClick={() => save.mutate()}>
            {save.isPending ? "Saving..." : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RedemptionsDialog({ coupon, onClose }: { coupon: any; onClose: () => void }) {
  const { data, isLoading, error } = useQuery({
    queryKey: ["coupon-redemptions", coupon.id],
    queryFn: () => adminApi.listCouponRedemptions(coupon.id),
  });
  const rows = data?.redemptions ?? [];
  const practised = rows.filter((r) => r.practisedSinceRedeeming).length;

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>Who used {coupon.code}</DialogTitle>
          <DialogDescription>
            {isLoading
              ? "Loading…"
              : `${rows.length} used it; ${practised} have done a practice interview since.`}
          </DialogDescription>
        </DialogHeader>
        {error && <p className="text-sm text-red-600">{errorMessage(error)}</p>}
        {rows.length > 0 && (
          <div className="max-h-96 overflow-auto">
            <table className="w-full text-left text-sm">
              <thead className="border-b text-slate-600">
                <tr>
                  <th className="py-2 pr-3 font-medium">Person</th>
                  <th className="py-2 pr-3 font-medium">Used on</th>
                  <th className="py-2 pr-3 font-medium">Pass</th>
                  <th className="py-2 font-medium">Practised</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} className="border-t">
                    <td className="py-2 pr-3">
                      <a className="underline" href={`/admin/users/${r.user.id}`}>
                        {r.user.email ?? r.user.id}
                      </a>
                      {r.user.name && <div className="text-xs text-slate-500">{r.user.name}</div>}
                    </td>
                    <td className="py-2 pr-3">{formatDay(r.redeemedAt)}</td>
                    <td className="py-2 pr-3">
                      {r.pass ? (
                        <>
                          {r.pass.planName} <Badge variant="outline">{r.pass.state}</Badge>
                          <div className="text-xs text-slate-500">
                            until {formatDay(r.pass.endsAt)}
                          </div>
                        </>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td className="py-2">{r.practisedSinceRedeeming ? "Yes" : "Not yet"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CopyLink({ link, compact = false }: { link?: string; compact?: boolean }) {
  const [copied, setCopied] = useState(false);
  if (!link) return null;
  return (
    <button
      type="button"
      className="inline-flex items-center gap-1 underline text-slate-600 hover:text-slate-900"
      title={link}
      onClick={async () => {
        await navigator.clipboard.writeText(link);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      }}
    >
      {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
      {copied ? "Copied" : compact ? "Copy link" : `Copy share link (${link})`}
    </button>
  );
}

function FormField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-xs text-slate-500 font-medium">{label}</span>
      {children}
    </label>
  );
}
